import { UserInterface } from "@swivl-mono/types";
import { parseFullName } from "parse-full-name";

interface HasName {
  name: string;
}

/**
 * Names that describe an organisation rather than a person. Splitting these
 * into first/last produces nonsense ("Acme" / "LLC"), so the storage path
 * leaves them alone.
 */
const BUSINESS_NAME_PATTERN =
  /\b(llc|l\.l\.c|inc|inc\.|incorporated|corp|corp\.|corporation|company|co\.|ltd|ltd\.|limited|trust|estate|foundation|associates|partners|holdings|group|properties|storage|rentals|services|solutions|enterprises)\b/i;
export class NameHelper {
  static parseFullName(user: HasName) {
    return parseFullName(user.name);
  }
  static setFirstName(user: HasName, firstName: string) {
    const parsed = parseFullName(user.name);
    return [firstName, parsed.middle, parsed.last].filter(Boolean).join(" ");
  }
  static setLastName(user: HasName, lastName: string) {
    const parsed = parseFullName(user.name);
    return [parsed.first, parsed.middle, lastName].filter(Boolean).join(" ");
  }

  static getFirstName(user: HasName) {
    return parseFullName(user.name).first || user.name;
  }

  static getLastName(user: HasName) {
    return parseFullName(user.name).last || user.name;
  }

  /**
   * Split a display name into first/last for the stored columns (ENG-3048).
   *
   * Returns null when we should NOT guess:
   *   - nothing to parse, or a synthetic "User 12345" placeholder;
   *   - a business-shaped name ("Acme Storage LLC", "Smith Family Trust") —
   *     inventing a surname for an entity is worse than storing nothing;
   *   - a single token, where "first vs last" is genuinely unknowable.
   *
   * This is the lazy-backfill path, so a guess we're unsure about must stay
   * unwritten rather than becoming a stored fact.
   */
  static splitForStorage(name?: string | null): { firstName: string; lastName: string } | null {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return null;
    if (/^User \d+/i.test(trimmed)) return null;
    if (BUSINESS_NAME_PATTERN.test(trimmed)) return null;

    const parsed = parseFullName(trimmed);
    const first = (parsed.first ?? "").trim();
    const last = (parsed.last ?? "").trim();
    if (!first || !last) return null;
    return { firstName: first, lastName: last };
  }

  static nameForUser(user?: UserInterface) {
    if (!user) {
      return "Unknown User";
    }
    if (user.name && user.name !== "") {
      return user.name.trim();
    }
    if (user.number) {
      return `User ${user.number}`;
    }
    if (user.id) {
      return `User ${user.id}`;
    }
    return "Unknown User";
  }
}
